import { Component, inject, signal, effect, ViewChild, AfterViewInit, ChangeDetectionStrategy } from '@angular/core';
import { DestroyRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { combineLatest } from 'rxjs';
import { SessionService } from '../../core/session.service';
import { ScanContextService } from '../../core/services/scan-context.service';
import { ForecastGraphComponent } from '../previsions/forecast-graph/forecast-graph.component';
import { AjustementManuelService, TimeSlot } from './ajustement-manuel.service';
import { ToastService } from '../../core/services/toast.service';
import { ForecastService, ForecastDto } from '../../core/services/forecast.service';
import { WizardConfigService } from '../../core/services/wizard-config.service';
import { TimezoneService } from '../../core/services/timezone.service';
import { ContextModalComponent } from './context-modal/context-modal.component';
import { SensorPresenceChartComponent } from './sensor-presence-chart/sensor-presence-chart.component';
import { SensorHistoricalPoint } from '../../core/services/sensor-historical.service';

@Component({
  standalone: true,
  selector: 'app-ajustement-manuel',
  imports: [CommonModule, FormsModule, ForecastGraphComponent, ContextModalComponent, SensorPresenceChartComponent, TranslatePipe],
  templateUrl: './ajustement-manuel.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./ajustement-manuel.component.scss']
})
export class AjustementManuelComponent implements AfterViewInit {
  @ViewChild(ForecastGraphComponent) forecastGraph!: ForecastGraphComponent;

  session = inject(SessionService);
  private ajustementService = inject(AjustementManuelService);
  private toastService = inject(ToastService);
  private forecastService = inject(ForecastService);
  private wizardConfigService = inject(WizardConfigService);
  private timezoneService = inject(TimezoneService);
  private scanContextService = inject(ScanContextService);
  private destroyRef = inject(DestroyRef);

  saving = signal<boolean>(false);
  sensorDataAvailable = signal<boolean>(true);
  selectedQueueId = signal<string | null>(null);
  selectedContextId: string | null = null;
  contexts = signal<{ id: string; name: string }[]>([]);
  selectedDay = signal<number>(this.timezoneService.getDateInEstablishmentTimezone().getDay());
  timeSlots = signal<TimeSlot[]>([]);
  hasChanges = signal<boolean>(false);
  showConfirmModal = signal<boolean>(false);
  showResetConfirmModal = signal<boolean>(false);
  showContextModal = signal<boolean>(false);
  forecastData = signal<ForecastDto | null>(null);
  wizardConfigured = signal<boolean>(false);
  loadingContexts = signal<boolean>(false);
  showBaseline = true;
  showHistorical = true;
  showSensorOnGraph = false;
  sensorFlowPoints: SensorHistoricalPoint[] = [];

  private lastLoadedKey = '';

  // Seuils Fluide/Modéré/Forte affluence — configurés dans Qualité·Objectifs (défaut org ou surcharge site)
  get lowTh(): number { return this.session.affluenceThresholds()?.waitLowMinutes ?? 10; }
  get highTh(): number { return this.session.affluenceThresholds()?.waitHighMinutes ?? 20; }

  private static readonly THRESHOLD_VALUES = [10, 15, 20, 25, 30, 40, 50, 60, 75, 90, 120, 150, 180, 240];

  openContextModal() {
    this.showContextModal.set(true);
  }

  closeContextModal() {
    this.showContextModal.set(false);
  }

  onContextModified() {
    const queueId = this.selectedQueueId();
    if (queueId) {
      this.loadingContexts.set(true);
      this.scanContextService.getContexts(queueId)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (fullContexts) => {
            this.contexts.set(fullContexts.map(c => ({ id: c.id, name: c.name })));
            const contextExists = fullContexts.some(c => c.id === this.selectedContextId);
            if (!contextExists) {
              const normaleContext = fullContexts.find(c => c.name === 'Normale');
              if (normaleContext) {
                this.selectedContextId = normaleContext.id;
              } else if (fullContexts.length > 0) {
                this.selectedContextId = fullContexts[0].id;
              }
            }
            if (this.forecastGraph) {
              this.forecastGraph.forceReload();
            }
            this.loadingContexts.set(false);
          },
          error: (err) => {
            console.error('Failed to reload contexts:', err);
            this.loadingContexts.set(false);
          }
        });
    }
  }

  days = [
    { value: 1, labelKey: 'common.days.shortByCode.mon' },
    { value: 2, labelKey: 'common.days.shortByCode.tue' },
    { value: 3, labelKey: 'common.days.shortByCode.wed' },
    { value: 4, labelKey: 'common.days.shortByCode.thu' },
    { value: 5, labelKey: 'common.days.shortByCode.fri' },
    { value: 6, labelKey: 'common.days.shortByCode.sat' },
    { value: 0, labelKey: 'common.days.shortByCode.sun' }
  ];

  constructor() {
    effect(() => {
      const queues = this.session.queues();
      if (queues && queues.length > 0) {
        const currentQueueId = this.selectedQueueId();
        const isValidQueue = !!currentQueueId && queues.some(q => q.id === currentQueueId);
        const selectedQueue = isValidQueue && currentQueueId ? queues.find(q => q.id === currentQueueId) : queues[0];
        if (selectedQueue) {
          this.selectedQueueId.set(selectedQueue.id);
          const queueContexts = selectedQueue.scan_contexts || [];
          this.contexts.set(queueContexts);
          const normaleContext = queueContexts.find(c => c.name === 'Normale');
          if (normaleContext) {
            this.selectedContextId = normaleContext.id;
          } else if (queueContexts.length > 0) {
            this.selectedContextId = queueContexts[0].id;
          }
        }
      }
    }, { allowSignalWrites: true });

    effect(() => {
      const queueId = this.selectedQueueId();
      const dayOfWeek = this.selectedDay();
    });

    effect(() => {
      const establishmentId = this.session.selectedId();
      if (establishmentId) {
        this.checkWizardStatus(establishmentId);
      }
    });
  }

  ngAfterViewInit() {
    setTimeout(() => {
      if (this.forecastGraph && this.forecastGraph.points().length > 0) {
        const graphData = this.forecastGraph.points();
        this.timeSlots.set(graphData);
        this.hasChanges.set(false);
      }
    }, 100);
  }

  private syncWithGraph() {
    if (this.forecastGraph) {
      const graphData = this.forecastGraph.points();
      this.timeSlots.set(graphData);
    }
  }

  getSelectedQueueName(): string {
    const queue = this.session.queues().find(q => q.id === this.selectedQueueId());
    return queue?.name || 'File d\'attente';
  }

  getSelectedContextName(): string {
    const context = this.contexts().find(c => c.id === this.selectedContextId);
    return context?.name || 'Normale';
  }

  selectDay(day: number) {
    this.selectedDay.set(day);
  }

  private loadForecastData(queueId: string, dayOfWeek: number): void {
    const cacheKey = `${queueId}-${dayOfWeek}`;
    if (this.lastLoadedKey === cacheKey) return;
    this.lastLoadedKey = cacheKey;
    this.forecastService.getDay(queueId, undefined, dayOfWeek)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data) => { this.forecastData.set(data); },
        error: (err) => {
          console.error('Failed to load forecast data:', err);
          this.forecastData.set(null);
          this.lastLoadedKey = '';
        }
      });
  }

  getBarPosition(index: number): number {
    const totalSlots = 32;
    const slotWidth = 100 / totalSlots;
    const slotCenter = slotWidth / 2;
    return (index / totalSlots) * 100 + slotCenter;
  }

  getBarHeightPx(minutes: number): string {
    if (!this.forecastGraph) return '8%';
    const yPx = this.forecastGraph.yToPx(minutes); // coordonnée viewBox (0–300)
    const H = 300;
    // Pourcentage depuis le bas du SVG — valide quelle que soit la hauteur rendue
    const pct = ((H - yPx) / H) * 100;
    return `${Math.max(0, pct).toFixed(2)}%`;
  }

  private nextNiceTopAbove(value: number): number {
    const found = AjustementManuelComponent.THRESHOLD_VALUES.find(t => t > value);
    if (found) return found;
    const step = 30;
    return Math.floor(value / step) * step + step;
  }

  private dragIndex: number | null = null;
  private startY: number = 0;
  private startMinutes: number = 0;

  startDrag(index: number, event: MouseEvent) {
    if (!this.forecastGraph) return;
    const timeSlotData = this.forecastGraph.timeSlotData;
    if (!timeSlotData[index] || !timeSlotData[index].open) return;
    event.preventDefault();
    this.dragIndex = index;
    this.startY = event.clientY;
    this.startMinutes = timeSlotData[index].minutes;
    document.addEventListener('mousemove', this.onMouseMove);
    document.addEventListener('mouseup', this.onMouseUp);
  }

  private onMouseMove = (event: MouseEvent) => {
    if (this.dragIndex === null || !this.forecastGraph) return;
    const deltaY = this.startY - event.clientY;
    const deltaMinutes = Math.round(deltaY / 3);
    const newMinutes = Math.max(1, Math.min(60, this.startMinutes + deltaMinutes));
    const timeSlotData = this.forecastGraph.timeSlotData;
    if (timeSlotData[this.dragIndex]) {
      timeSlotData[this.dragIndex].minutes = newMinutes;
      this.checkForChanges();
    }
  };

  private onMouseUp = () => {
    this.dragIndex = null;
    document.removeEventListener('mousemove', this.onMouseMove);
    document.removeEventListener('mouseup', this.onMouseUp);
  };

  adjustMinutes(index: number, delta: number) {
    if (!this.forecastGraph) return;
    const timeSlotData = this.forecastGraph.timeSlotData;
    if (timeSlotData[index]) {
      timeSlotData[index].minutes = Math.max(1, Math.min(60, timeSlotData[index].minutes + delta));
      this.checkForChanges();
    }
  }

  isEstablishmentClosed(): boolean {
    return this.forecastGraph?.isEstablishmentClosed() ?? false;
  }

  isWizardConfigured(): boolean {
    return this.wizardConfigured();
  }

  private checkWizardStatus(establishmentId: string): void {
    this.wizardConfigService.getEstablishmentConfig(establishmentId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (configs) => {
          const configuredQueue = configs.find(q => q.config);
          this.wizardConfigured.set(!!configuredQueue);
        },
        error: (error) => {
          console.error('Failed to check wizard status:', error);
          this.wizardConfigured.set(false);
        }
      });
  }

  private checkForChanges() {
    this.hasChanges.set(true);
  }

  cancelChanges() {
    this.lastLoadedKey = '';
    const queueId = this.selectedQueueId();
    if (queueId) {
      this.loadForecastData(queueId, this.selectedDay());
    }
    this.hasChanges.set(false);
  }

  saveConfig() {
    if (!this.hasChanges()) {
      this.toastService.show('Aucun changement détecté', 'info', 3000, 'Les temps d\'attente n\'ont pas été modifiés');
      return;
    }
    this.showConfirmModal.set(true);
  }

  confirmSave() {
    const queueId = this.selectedQueueId();
    if (!queueId || !this.forecastGraph || this.isEstablishmentClosed()) return;
    const points: TimeSlot[] = this.forecastGraph.timeSlotData.map(slot => ({
      time: slot.label,
      minutes: slot.minutes
    }));
    const forecastDate = this.getTargetDateForSelectedDay();
    this.saving.set(true);
    this.showConfirmModal.set(false);
    this.ajustementService.saveManualForecast(queueId, forecastDate, points)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.saving.set(false);
          this.hasChanges.set(false);
          this.toastService.show('C\'est noté. Merci 👋', 'success', 4000, 'Ajustements sauvegardés avec succès');
        },
        error: (err) => {
          console.error('Save error - Queue ID:', queueId?.substring(0, 8), 'Date:', forecastDate);
          this.saving.set(false);
          this.toastService.show('Erreur', 'error', 4000, 'Erreur lors de la sauvegarde');
        }
      });
  }

  cancelSave() {
    this.showConfirmModal.set(false);
  }

  resetToBaseline() {
    this.showResetConfirmModal.set(true);
  }

  confirmReset() {
    const queueId = this.selectedQueueId();
    if (!queueId) return;
    const date = this.getTargetDateForSelectedDay();
    this.showResetConfirmModal.set(false);
    this.ajustementService.deleteManualForecast(queueId, date)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          if (this.forecastGraph) {
            this.forecastGraph.forceReload();
          }
          this.hasChanges.set(false);
          this.toastService.show('Réinitialisation effectuée', 'success', 3000, 'Retour au calcul automatique');
        },
        error: (err) => {
          console.error('Reset error - Queue ID:', queueId?.substring(0, 8), 'Date:', date);
          this.toastService.show('Erreur', 'error', 4000, 'Erreur lors de la réinitialisation');
        }
      });
  }

  cancelReset() {
    this.showResetConfirmModal.set(false);
  }

  refreshData() {
    const queueId = this.selectedQueueId();
    const dayOfWeek = this.selectedDay();
    if (queueId) {
      this.lastLoadedKey = '';
      this.loadForecastData(queueId, dayOfWeek);
      this.hasChanges.set(false);
    }
  }

  private getTargetDateForSelectedDay(): string {
    const today = new Date();
    const selectedDayOfWeek = this.selectedDay();
    const currentDayOfWeek = today.getDay();
    const daysToAdd = (selectedDayOfWeek - currentDayOfWeek + 7) % 7;
    const targetDate = new Date(today);
    targetDate.setDate(today.getDate() + daysToAdd);
    return this.timezoneService.formatDateForAPI(targetDate);
  }

  toggleBaseline() {}
  toggleHistorical() {}
}
